import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSyncEvents } from '../api';
import { formatTimestamp, formatRelativeTime } from '../utils';
import { useFilters } from '../hooks';
import type { 
  SyncEvent, 
  SyncEventFilters,
  ConflictCreatedResponseSummary,
  ConflictResolvedResponseSummary,
  ConflictAction
} from '../types';
import { EVENT_TYPES, CONFLICT_ACTION_LABELS } from '../types';
import styles from './Conflicts.module.css';

/**
 * Conflicts Page
 * 
 * Shows file conflict events - when device and server versions clash
 * and how they were resolved.
 */

type ConflictFilter = 'all' | 'created' | 'resolved' | 'pending';

export default function Conflicts() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [search, setSearch] = useState('');
  const [conflictFilter, setConflictFilter] = useState<ConflictFilter>('all');
  const { timeRange, setTimeRange } = useFilters();
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const baseFilters: SyncEventFilters = {
        page,
        page_size: pageSize,
        ordering: '-timestamp',
      };

      if (search.trim()) {
        if (search.includes('@')) {
          baseFilters.user_email = search.trim();
        } else {
          baseFilters.device_code = search.trim();
        }
      }

      if (timeRange) {
        const now = new Date();
        let since: Date;
        switch (timeRange) {
          case '1h':
            since = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case '24h':
            since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            since = new Date(0);
        }
        baseFilters.timestamp_gte = since.toISOString();
      }

      let allEvents: SyncEvent[] = [];

      // For "all" conflicts, fetch both types separately since backend may not support event_type__in
      if (conflictFilter === 'all') {
        const [createdResponse, resolvedResponse] = await Promise.all([
          getSyncEvents({ ...baseFilters, event_type: EVENT_TYPES.CONFLICT_CREATED }),
          getSyncEvents({ ...baseFilters, event_type: EVENT_TYPES.CONFLICT_RESOLVED }),
        ]);
        
        allEvents = [
          ...(createdResponse.results || []),
          ...(resolvedResponse.results || []),
        ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        console.log(`[Conflicts] Fetched ${createdResponse.results?.length || 0} created + ${resolvedResponse.results?.length || 0} resolved events`);
      } else {
        // Single type filter
        const eventType = conflictFilter === 'created' || conflictFilter === 'pending' 
          ? EVENT_TYPES.CONFLICT_CREATED 
          : EVENT_TYPES.CONFLICT_RESOLVED;
        
        const response = await getSyncEvents({ ...baseFilters, event_type: eventType });
        allEvents = response.results || [];
      }
      
      // Filter client-side as a defensive measure in case backend doesn't filter properly
      const conflictEvents = allEvents.filter(
        e => e.event_type === EVENT_TYPES.CONFLICT_CREATED || e.event_type === EVENT_TYPES.CONFLICT_RESOLVED
      );
      
      // Log warning if backend returned non-conflict events (indicates backend filter issue)
      if (allEvents.length > 0 && conflictEvents.length !== allEvents.length) {
        console.warn(
          `[Conflicts] Backend returned ${allEvents.length} events but only ${conflictEvents.length} are actual conflict events. ` +
          `Backend may not be filtering by event_type correctly. ` +
          `Event types received: ${[...new Set(allEvents.map(e => e.event_type))].join(', ')}`
        );
      }
      
      setEvents(conflictEvents);
      setTotalCount(conflictEvents.length);
    } catch (err) {
      console.error('[Conflicts] Error fetching:', err);
      setError('Failed to load conflict events');
    } finally {
      setIsLoading(false);
    }
  }, [search, conflictFilter, timeRange, page]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    setPage(1);
  }, [search, conflictFilter, timeRange]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const getConflictIcon = (event: SyncEvent): string => {
    if (event.event_type === EVENT_TYPES.CONFLICT_CREATED) {
      return '⚡';
    }
    return '✅';
  };

  const getActionLabel = (action: ConflictAction | undefined): string => {
    if (!action) return 'Unknown';
    return CONFLICT_ACTION_LABELS[action] || action;
  };

  const getActionIcon = (action: ConflictAction | undefined): string => {
    switch (action) {
      case 'overwrite_server': return '📱→☁️';
      case 'keep_both': return '📱+☁️';
      case 'discard_device': return '☁️→📱';
      default: return '❓';
    }
  };

  const renderConflictCreated = (event: SyncEvent) => {
    const response = event.response_summary as ConflictCreatedResponseSummary | undefined;
    
    return (
      <div className={styles.conflictDetails}>
        <div className={styles.conflictReason}>
          <span className={styles.reasonLabel}>Reason:</span>
          <span className={styles.reasonText}>{response?.reason || 'Version conflict detected'}</span>
        </div>
        <div className={styles.conflictIds}>
          {response?.conflict_id && (
            <div className={styles.idItem}>
              <span className={styles.idLabel}>Conflict ID</span>
              <span className={styles.idValue}>{response.conflict_id.slice(0, 12)}...</span>
            </div>
          )}
          {response?.server_file_id && (
            <div className={styles.idItem}>
              <span className={styles.idLabel}>Server File</span>
              <span className={styles.idValue}>{response.server_file_id.slice(0, 12)}...</span>
            </div>
          )}
          {response?.device_file_id && (
            <div className={styles.idItem}>
              <span className={styles.idLabel}>Device File</span>
              <span className={styles.idValue}>{response.device_file_id.slice(0, 12)}...</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConflictResolved = (event: SyncEvent) => {
    const response = event.response_summary as ConflictResolvedResponseSummary | undefined;
    
    return (
      <div className={styles.conflictDetails}>
        <div className={styles.resolution}>
          <span className={styles.actionIcon}>{getActionIcon(response?.action_taken)}</span>
          <div className={styles.actionInfo}>
            <span className={styles.actionLabel}>{getActionLabel(response?.action_taken)}</span>
            <span className={styles.actionOutcome}>{response?.outcome || 'Conflict resolved'}</span>
          </div>
        </div>
      </div>
    );
  };

  // Count created vs resolved
  const createdCount = events.filter(e => e.event_type === EVENT_TYPES.CONFLICT_CREATED).length;
  const resolvedCount = events.filter(e => e.event_type === EVENT_TYPES.CONFLICT_RESOLVED).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <span className={styles.titleIcon}>⚡</span>
            File Conflicts
          </h1>
          <p className={styles.subtitle}>
            {totalCount.toLocaleString()} conflict events
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <div 
          className={`${styles.statCard} ${conflictFilter === 'created' ? styles.statCardActive : ''}`}
          onClick={() => setConflictFilter(conflictFilter === 'created' ? 'all' : 'created')}
        >
          <span className={styles.statIcon}>⚡</span>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{createdCount}</span>
            <span className={styles.statLabel}>Created</span>
          </div>
        </div>
        <div 
          className={`${styles.statCard} ${styles.statCardResolved} ${conflictFilter === 'resolved' ? styles.statCardActive : ''}`}
          onClick={() => setConflictFilter(conflictFilter === 'resolved' ? 'all' : 'resolved')}
        >
          <span className={styles.statIcon}>✅</span>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{resolvedCount}</span>
            <span className={styles.statLabel}>Resolved</span>
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search by user email or device code..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filterGroup}>
          <select 
            className={styles.select}
            value={conflictFilter}
            onChange={(e) => setConflictFilter(e.target.value as ConflictFilter)}
          >
            <option value="all">All Conflicts</option>
            <option value="created">⚡ Created Only</option>
            <option value="resolved">✅ Resolved Only</option>
          </select>
          <select 
            className={styles.select}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="">All Time</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      <div className={styles.tableContainer}>
        {isLoading ? (
          <div className={styles.loading}>Loading conflict events...</div>
        ) : (
          <>
            {events.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>✨</div>
                <h3>No Conflicts</h3>
                <p>No file conflicts found matching your filters.</p>
              </div>
            ) : (
              <div className={styles.timeline}>
                {events.map((event) => {
                  const isCreated = event.event_type === EVENT_TYPES.CONFLICT_CREATED;
                  
                  return (
                    <div 
                      key={event.id} 
                      className={`${styles.timelineItem} ${isCreated ? styles.conflictCreated : styles.conflictResolved}`}
                    >
                      <div className={styles.timelineMarker}>
                        <span className={styles.timelineIcon}>{getConflictIcon(event)}</span>
                      </div>
                      
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineHeader}>
                          <span className={styles.eventType}>
                            {isCreated ? 'Conflict Created' : 'Conflict Resolved'}
                          </span>
                          <span className={styles.timestamp} title={formatTimestamp(event.timestamp)}>
                            {formatRelativeTime(event.timestamp)}
                          </span>
                        </div>

                        <div className={styles.fileInfo}>
                          {event.file_name && (
                            <span className={styles.fileName}>📄 {event.file_name}</span>
                          )}
                          {event.project_name && (
                            <span className={styles.projectName}>in {event.project_name}</span>
                          )}
                        </div>

                        {isCreated ? renderConflictCreated(event) : renderConflictResolved(event)}

                        <div className={styles.timelineMeta}>
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>User:</span>
                            {event.user_email || '-'}
                          </span>
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>Device:</span>
                            <span className={styles.deviceCode}>{event.device_code || '-'}</span>
                          </span>
                        </div>

                        <button 
                          className={styles.viewButton}
                          onClick={() => navigate(`/events/${event.id}`)}
                        >
                          View Full Details →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

